import { h, JSX } from 'preact'

import styles from './Tendens.module.css'
import { format, message, Message } from '../../../../lib/intl/Message'
import { Geografi, Parti, Valg, Valgtype } from '../../../domain'
import { OppslutningTableItem } from '../Oppslutning/mapping'

import { useLocale } from '../../../../lib/intl/IntlProvider'
import { calculateBarWidthPercent } from '../Oppslutning/OppslutningTable/OppslutningTable'
import { FormattedNumber } from '../../../../lib/intl/FormattedNumber'
import { bem } from '@nrk/dh-utils'
import { TabHeading } from '../../shared/Tabs/TabHeading'
import { TabPanel, Tabs } from '../../shared/Tabs/Tabs'
import { withSectionErrorBoundary } from '../../shared/SectionErrorBoundary/SectionErrorBoundary'
import { useWidgetUrls } from '../../../routes/widget'
import { idFrom } from '../../../api'
import { calculateTendens, getTekstId, TekstProps } from './tendens-calulations'

type Pallplassering = {
  id: 'BORGERLIGE' | 'SOSIALISTISKE' | 'ANDRE'
  label: string
  tendens: {
    oppslutning: number
    oppslutningForrige: number
    endring: number
  }
  legend: string
  farge: string
}

type Props = {
  grunndata: Geografi
  items: OppslutningTableItem[]
  valgtype: 'kommune' | 'fylke'
  widgetFooter?: JSX.Element
}

export function _Tendens({ grunndata, items, valgtype, widgetFooter }: Props) {
  const locale = useLocale()
  const { tendensUrl } = useWidgetUrls()
  const { navn } = grunndata
  const tendens = calculateTendens(items)
  if (tendens.andre.oppslutning < 0) {
    // Skjuler tendens når oppslutning andre er negativ
    return null
  }

  const valg =
    valgtype === 'kommune' ? message('tendens-kommunevalg') : message('tendens-fylkesvalg')

  const blokker = [
    {
      id: 'SOSIALISTISKE',
      label: message('tendens-label-sosialistiske'),
      tendens: tendens.sosialistiske,
      legend: format('{partier}', locale, { partier: tendens.sosialistiske.partier }),
      farge: '#e51c30'
    },
    {
      id: 'BORGERLIGE',
      label: message('tendens-label-borgerlige'),
      tendens: tendens.borgerlige,
      legend: format('{partier}', locale, { partier: tendens.borgerlige.partier }),
      farge: '#00b8f1'
    },
    {
      id: 'ANDRE',
      label: message('tendens-label-andre'),
      tendens: tendens.andre,
      legend: message('tendens-legend-andre'),
      farge: '#96989a'
    }
  ] as Pallplassering[]

  const maksProsent = Math.max(60, ...blokker.map(item => item.tendens.oppslutning || 0))
  const vinnerId = blokker.slice(0).sort((a, b) => b.tendens.oppslutning - a.tendens.oppslutning)[0]
    .id
  const vinnerIdForrige = blokker
    .slice(0)
    .sort((a, b) => b.tendens.oppslutningForrige - a.tendens.oppslutningForrige)[0].id

  return (
    <section
      class={styles.tendens}
      data-widget-url={tendensUrl(valgtype)}
      data-valgtype={valgtype}
      data-grunndata={idFrom(grunndata)}
      data-modulnavn="tendens"
    >
      <h3>
        <Message id="tendens-tittel" values={{ navn }} />
      </h3>
      <p>
        {tekst({
          endringBorgerlige: tendens.borgerlige.endring,
          endringSosialistiske: tendens.sosialistiske.endring,
          endringAndre: tendens.andre.endring,
          oppslutningAndre: tendens.andre.oppslutning,
          oppslutningBorgerlige: tendens.borgerlige.oppslutning,
          oppslutningSosialistiske: tendens.sosialistiske.oppslutning,
          valg
        })}
      </p>
      <Tabs
        headings={[
          <TabHeading icon="tab-stemmer">
            <Message id={`tendens-tab-tittel-dette`} />
          </TabHeading>,
          <TabHeading icon="tab-kalender">
            <Message id={`tendens-tab-tittel-forrige`} />
          </TabHeading>
        ]}
      >
        <TabPanel>
          <div class={styles.palle}>
            {blokker.map(result => {
              const barHeight =
                calculateBarWidthPercent(result.tendens.oppslutning, maksProsent) / 100
              return (
                <div class={styles.plassering}>
                  <div class={bem(styles.oppslutning, { forste: result.id === vinnerId })}>
                    <FormattedNumber
                      value={result.tendens.oppslutning}
                      options={{
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1
                      }}
                    />{' '}
                    %
                  </div>
                  <div
                    class={styles.bar}
                    style={{
                      // Kalkulerer ut base size direkte. IE støtter ikke var
                      height: `calc(${barHeight.toFixed(2)} * 5 * 1.625 * (1.125rem + 0.1vw))`,
                      backgroundColor: result.farge
                    }}
                  />
                  <div class={styles.label}>{result.label}</div>
                </div>
              )
            })}
          </div>
          <Legend palle={blokker} svak={false} />
        </TabPanel>
        <TabPanel hidden>
          <div class={styles.palle}>
            {blokker.map(result => {
              const barHeight =
                calculateBarWidthPercent(result.tendens.oppslutningForrige, maksProsent) / 100
              return (
                <div class={styles.plassering}>
                  <div class={bem(styles.oppslutning, { forste: result.id === vinnerIdForrige })}>
                    <FormattedNumber
                      value={result.tendens.oppslutningForrige}
                      options={{
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1
                      }}
                    />{' '}
                    %
                  </div>
                  <div
                    class={styles.bar}
                    style={{
                      height: `calc(${barHeight.toFixed(2)} * 5 * 1.625 * (1.125rem + 0.1vw))`,
                      backgroundColor: result.farge
                    }}
                  />
                  <div class={styles.label}>{result.label}</div>
                </div>
              )
            })}
          </div>
          <Legend palle={blokker} svak={true} />
        </TabPanel>
      </Tabs>
      {widgetFooter}
    </section>
  )
}

type LegendProps = {
  palle: Pallplassering[]
  svak: boolean
}

function Legend({ palle, svak }: LegendProps) {
  return (
    <div class={styles.legends}>
      {palle.map(result => {
        return (
          <div class={styles.legend}>
            <span
              class={styles.box}
              style={{
                backgroundColor: result.farge
              }}
            />
            {result.legend}
          </div>
        )
      })}
    </div>
  )
}

function tekst(props: TekstProps) {
  return message(getTekstId(props), props)
}

export const Tendens = withSectionErrorBoundary(_Tendens)
